import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

/**
 * LegalGate — the first gate after login. If the caller is an eligible
 * administrator whose company still has required legal documents
 * unaccepted, redirect to /legal/accept. Otherwise pass through so the
 * subsequent OnboardingGate can decide about the onboarding wizard.
 *
 * Behaviour matches the backend rules exactly:
 *   • Super Admin → never redirected (skipped client-side too).
 *   • Operational roles → backend returns pending_required=[]; no
 *     redirect fires.
 *   • Legacy customers → backend returns pending_required=[]; no
 *     redirect fires. They remain unblocked but their audit trail
 *     remains empty until their next electronic acceptance.
 *   • Eligible admin with pending required docs → redirect.
 *
 * Serialization contract (2026-02-27): LegalGate MUST resolve before
 * OnboardingGate mounts. This prevents the previous race where a fast
 * /onboarding/state response could win the navigation over a slower
 * /legal/status response, and the user would land on /onboarding
 * without ever seeing Legal Onboarding. We hold the <Outlet /> render
 * (and therefore prevent OnboardingGate from mounting) until legal
 * status is decided.
 *
 * Never blocks render on a transport error — logs to console.warn so
 * support can trace missed redirects from session recordings, and
 * lets the app through (fail-open) so a Sentry blip cannot lock a
 * customer out of their kitchen.
 */
export default function LegalGate() {
    const { user } = useApp();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [ready, setReady] = useState(false);

    useEffect(() => {
        // No user yet (transient during login redirect) — keep gate
        // closed so the child tree does not mount prematurely.
        if (!user) {
            setReady(false);
            return;
        }
        // /legal/* is a top-level route outside this gate, but keep the
        // guard defensively in case the routing ever nests them.
        if (pathname.startsWith("/legal")) {
            setReady(true);
            return;
        }
        // Super Admin bypass — no legal check, gate opens immediately.
        if (user.role === "super_admin") {
            setReady(true);
            return;
        }
        let cancelled = false;
        setReady(false);
        api.get("/legal/status").then(({ data }) => {
            if (cancelled) return;
            if (data?.eligible
                && (data.pending_required || []).length > 0) {
                navigate("/legal/accept", { replace: true });
                // Intentionally do NOT setReady(true) — we are
                // navigating away; the gate stays closed until unmount
                // so OnboardingGate never mounts on this route.
                return;
            }
            setReady(true);
        }).catch((err) => {
            // Fail-open: log and let the app render. A transport
            // hiccup must never trap a customer in an infinite loader.
            console.warn("[LegalGate] status lookup failed",
                          err?.message || err);
            if (!cancelled) setReady(true);
        });
        return () => { cancelled = true; };
    }, [user, pathname, navigate]);

    // While the decision is pending we render nothing. This mirrors
    // the app's other "waiting for auth" states (see ProtectedRoute)
    // and, critically, prevents the nested <OnboardingGate /> from
    // mounting and firing its own /onboarding/state request in a race
    // with our /legal/status.
    if (!ready) return null;
    return <Outlet />;
}
